import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, Save, ArrowLeft, Package as PackageIcon } from "lucide-react";

const SIZE_UNITS = ["cm", "inch", "meter"];
const WEIGHT_UNITS = ["grams", "kilograms"];
const VOLUME_UNITS = ["ml", "liter", "cubic meter"];

export default function AddProduct() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEdit = Boolean(productId);

  const [form, setForm] = useState({
    name: "",
    description: "",
    photo: "",
    measurement_type: "SIZE",
    length: "",
    width: "",
    height: "",
    size_unit: "cm",
    weight_value: "",
    weight_unit: "kilograms",
    volume_value: "",
    volume_unit: "ml",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/store/products/${productId}`)
      .then(r => {
        const p = r.data;
        setForm({
          name: p.name || "",
          description: p.description || "",
          photo: p.photo || "",
          measurement_type: p.measurement_type || "SIZE",
          length: p.length ?? "",
          width: p.width ?? "",
          height: p.height ?? "",
          size_unit: p.size_unit || "cm",
          weight_value: p.weight_value ?? "",
          weight_unit: p.weight_unit || "kilograms",
          volume_value: p.volume_value ?? "",
          volume_unit: p.volume_unit || "ml",
        });
      })
      .catch(e => toast.error(formatApiError(e)));
  }, [isEdit, productId]);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) {
      toast.error("Image too large. Please choose one under 1MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description,
      photo: form.photo,
      measurement_type: form.measurement_type,
      length: form.measurement_type === "SIZE" ? Number(form.length) || null : null,
      width: form.measurement_type === "SIZE" ? Number(form.width) || null : null,
      height: form.measurement_type === "SIZE" ? Number(form.height) || null : null,
      size_unit: form.measurement_type === "SIZE" ? form.size_unit : null,
      weight_value: form.measurement_type === "WEIGHT" ? Number(form.weight_value) || null : null,
      weight_unit: form.measurement_type === "WEIGHT" ? form.weight_unit : null,
      volume_value: form.measurement_type === "VOLUME" ? Number(form.volume_value) || null : null,
      volume_unit: form.measurement_type === "VOLUME" ? form.volume_unit : null,
    };
    if (!payload.name) {
      toast.error("Product name is required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/store/products/${productId}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/store/products", payload);
        toast.success("Product created.");
      }
      navigate("/store/products");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="add-product-page" className="space-y-6 max-w-3xl">
      <button
        onClick={() => navigate("/store/products")}
        data-testid="back-to-products"
        className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white"
      >
        <ArrowLeft className="w-3 h-3" /> Back to Products
      </button>
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">
          / {isEdit ? "Edit Product" : "Add Product"}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {isEdit ? "Edit product" : "Add a new product"}
        </h1>
      </div>

      <form onSubmit={onSubmit} className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-6">
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-2 block">Photo</Label>
            <label className="block aspect-square rounded-md border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 cursor-pointer overflow-hidden group hover:border-cyan-500/40 transition-colors relative">
              {form.photo ? (
                <img src={form.photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 grid place-items-center text-slate-500">
                  <div className="flex flex-col items-center gap-1 text-xs">
                    <ImagePlus className="w-6 h-6" />
                    <span>Upload</span>
                  </div>
                </div>
              )}
              <input
                data-testid="product-photo-input"
                type="file"
                accept="image/*"
                onChange={onPhoto}
                className="hidden"
              />
            </label>
            {form.photo && (
              <button
                type="button"
                data-testid="clear-product-photo"
                onClick={() => setForm(f => ({ ...f, photo: "" }))}
                className="text-xs text-slate-500 hover:text-red-400 mt-2"
              >
                Remove photo
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Product Name</Label>
              <Input
                data-testid="product-name-input"
                required
                value={form.name}
                onChange={change("name")}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Description</Label>
              <Textarea
                data-testid="product-desc-input"
                value={form.description}
                onChange={change("description")}
                rows={3}
                className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1.5"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
          <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-3 block">
            Measurement Type
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {["SIZE", "WEIGHT", "VOLUME"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`mtype-${t}`}
                onClick={() => setForm({ ...form, measurement_type: t })}
                className={[
                  "px-4 py-3 rounded-md border text-sm font-medium uppercase tracking-wider transition-colors",
                  form.measurement_type === t
                    ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                    : "bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-100 hover:dark:bg-slate-900",
                ].join(" ")}
              >
                {t.toLowerCase()}
              </button>
            ))}
          </div>

          <div className="mt-5">
            {form.measurement_type === "SIZE" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <FieldInput label="Length" testid="size-length" value={form.length} onChange={change("length")} />
                <FieldInput label="Width" testid="size-width" value={form.width} onChange={change("width")} />
                <FieldInput label="Height" testid="size-height" value={form.height} onChange={change("height")} />
                <FieldSelect
                  label="Unit" testid="size-unit"
                  value={form.size_unit}
                  onChange={(v) => setForm({ ...form, size_unit: v })}
                  options={SIZE_UNITS}
                />
              </div>
            )}
            {form.measurement_type === "WEIGHT" && (
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Weight Value" testid="weight-value" value={form.weight_value} onChange={change("weight_value")} />
                <FieldSelect
                  label="Unit" testid="weight-unit"
                  value={form.weight_unit}
                  onChange={(v) => setForm({ ...form, weight_unit: v })}
                  options={WEIGHT_UNITS}
                />
              </div>
            )}
            {form.measurement_type === "VOLUME" && (
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Volume Value" testid="volume-value" value={form.volume_value} onChange={change("volume_value")} />
                <FieldSelect
                  label="Unit" testid="volume-unit"
                  value={form.volume_unit}
                  onChange={(v) => setForm({ ...form, volume_unit: v })}
                  options={VOLUME_UNITS}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-800">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/store/products")}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            data-testid="save-product-btn"
            disabled={saving}
            className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : (isEdit ? "Update Product" : "Save Product")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FieldInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        data-testid={testid}
        type="number"
        step="any"
        value={value}
        onChange={onChange}
        className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 mt-1"
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          {options.map(o => (
            <SelectItem key={o} value={o} data-testid={`${testid}-opt-${o}`}>{o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
